import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  FaGift,
  FaUsers,
  FaRupeeSign,
  FaCopy,
  FaCheck,
  FaShareAlt,
  FaWhatsapp,
  FaShoppingBag,
  FaShieldAlt,
} from "react-icons/fa";
import { apiFetch } from "../../../utils/apiFetch";

const ReferralSection = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const json = await apiFetch("api/v1/customer/referral");
        const data = json.data ?? json;
        if (isMounted) setReferralData(data);
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to load referral details");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const referralCode =
    referralData?.referral_code ??
    referralData?.code ??
    referralData?.my_referral_code ??
    "—";

  const totalReferrals =
    referralData?.total_referrals ??
    referralData?.referral_count ??
    referralData?.referred_count ??
    0;

  const totalEarned =
    referralData?.total_earned ??
    referralData?.wallet_earned ??
    referralData?.total_reward ??
    0;

  const appUrl = window.location.origin;
  const shareMessage = `Hey! Order pure, farm-fresh milk & groceries daily from Kisan Daily Fresh. Use my referral code: *${referralCode}* when you sign up to get special discounts! 🌱🥛\n\nDownload or order now: ${appUrl}`;

  const copyToClipboard = () => {
    if (!referralCode || referralCode === "—") return;
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success("Referral code copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    if (!referralCode || referralCode === "—") return;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleNativeShare = async () => {
    if (!referralCode || referralCode === "—") return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Kisan Daily Fresh Referral",
          text: shareMessage,
          url: appUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          navigator.clipboard.writeText(shareMessage);
          toast.success("Invite message copied to clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(shareMessage);
      toast.success("Invite message copied to clipboard!");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-4xl font-sans">
      {/* ── 1. Hero Banner ── */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#03522b] via-[#046a38] to-emerald-700 p-5 sm:p-7 text-white shadow-md">
        {/* Decorative ambient background rings */}
        <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-10 -left-10 h-44 w-44 rounded-full bg-emerald-400/20 blur-3xl" />

        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2 max-w-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold backdrop-blur-md">
              <FaGift className="h-3.5 w-3.5 text-amber-300" />
              <span>Refer & Earn Program</span>
            </span>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight leading-tight">
              Share the Goodness, <br className="hidden sm:inline" />
              Earn Wallet Cash!
            </h1>
            <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed">
              Invite your friends and family to Kisan Daily Fresh. When they place
              their first order, both of you earn rewards directly in your wallet!
            </p>
          </div>

          <div className="hidden sm:flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md">
            <FaGift className="h-12 w-12 text-amber-300 drop-shadow-sm" />
          </div>
        </div>
      </div>

      {/* ── 2. Referral Code & Sharing Card ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm sm:text-base font-bold text-gray-900">
              Your Unique Referral Code
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Tap below to copy or share directly with friends
            </p>
          </div>
          <span className="self-start sm:self-auto rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-[#046a38] border border-emerald-200">
            Unlimited Invites
          </span>
        </div>

        {loading ? (
          <div className="py-6 flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="mt-3 rounded-xl bg-red-50 p-3 text-xs text-red-600 font-medium">
            {error}
          </div>
        ) : (
          <div className="mt-4 space-y-3.5">
            {/* Code Box with 1-click copy */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div
                onClick={copyToClipboard}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && copyToClipboard()}
                className="group relative flex flex-1 items-center justify-between rounded-xl border-2 border-dashed border-[#046a38]/40 bg-emerald-50/40 px-4 py-3 cursor-pointer transition hover:bg-emerald-50 hover:border-[#046a38] active:scale-[0.99]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#046a38] text-white">
                    <FaGift className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Tap Code to Copy
                    </span>
                    <span className="text-base sm:text-lg font-black tracking-widest text-[#046a38]">
                      {referralCode}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-[#046a38] shadow-sm border border-emerald-100 group-hover:bg-[#046a38] group-hover:text-white transition">
                  {copied ? (
                    <>
                      <FaCheck className="h-3 w-3" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <FaCopy className="h-3 w-3" />
                      <span>Copy</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Sharing Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              <button
                type="button"
                onClick={handleWhatsAppShare}
                className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-xs sm:text-sm font-bold text-white shadow-sm hover:bg-[#20ba59] transition active:scale-95"
              >
                <FaWhatsapp className="h-4 w-4 text-white" />
                <span>Share via WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={handleNativeShare}
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-xs sm:text-sm font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition active:scale-95"
              >
                <FaShareAlt className="h-3.5 w-3.5 text-gray-600" />
                <span>More Share Options</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Stats Summary Cards ── */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {/* Friends Invited */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm flex items-center gap-3 sm:gap-4">
          <div className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <FaUsers className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0">
            <span className="block text-lg sm:text-2xl font-black text-gray-900 leading-tight">
              {loading ? "…" : totalReferrals}
            </span>
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500">
              Friends Joined
            </span>
          </div>
        </div>

        {/* Total Earned */}
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 sm:p-5 shadow-sm flex items-center gap-3 sm:gap-4">
          <div className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-[#046a38]">
            <FaRupeeSign className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0">
            <span className="block text-lg sm:text-2xl font-black text-[#046a38] leading-tight">
              ₹{loading ? "…" : Number(totalEarned).toFixed(0)}
            </span>
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500">
              Total Rewards
            </span>
          </div>
        </div>
      </div>

      {/* ── 4. How It Works (Mobile-First Step Flow) ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
        <h2 className="text-sm sm:text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>How Referral Works</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#046a38]" />
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {/* Step 1 */}
          <div className="relative flex flex-col justify-between rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 sm:p-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#046a38] text-xs font-black text-white">
                  1
                </span>
                <FaShareAlt className="h-3.5 w-3.5 text-gray-400" />
              </div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                Share Invite
              </h3>
              <p className="mt-1 text-[11px] sm:text-xs text-gray-500 leading-relaxed">
                Send your unique code or link to friends & family over WhatsApp or SMS.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative flex flex-col justify-between rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 sm:p-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#046a38] text-xs font-black text-white">
                  2
                </span>
                <FaShoppingBag className="h-3.5 w-3.5 text-gray-400" />
              </div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                They Place Order
              </h3>
              <p className="mt-1 text-[11px] sm:text-xs text-gray-500 leading-relaxed">
                Friend enters your referral code while ordering farm-fresh groceries.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative flex flex-col justify-between rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 sm:p-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#046a38] text-xs font-black text-white">
                  3
                </span>
                <FaGift className="h-3.5 w-3.5 text-amber-500" />
              </div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                You Both Earn
              </h3>
              <p className="mt-1 text-[11px] sm:text-xs text-gray-500 leading-relaxed">
                Reward credits are added instantly to your wallet to spend on future orders!
              </p>
            </div>
          </div>
        </div>

        {/* Reassurance Banner */}
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50/60 border border-emerald-100 px-3.5 py-2.5 text-xs text-gray-600">
          <FaShieldAlt className="h-4 w-4 text-[#046a38] shrink-0" />
          <span>
            Rewards are automatically applied at checkout. No limit on total referral rewards.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReferralSection;
